/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import {
    Icon,
    Badge,
    Button,
    FlexRow,
    DividerNew as Divider,
    FlexCol,
    Heading,
    withLDConsumer,
} from 'sarsaparilla';

import SearchFilterStartTime from '../components/SearchFilterStartTime';
import * as searchActions from '../actions/search';
import SearchFilterTourType from '../components/SearchFilterTourType';
import { SearchFilterTourDuration } from '../components/SearchFilterTourDuration';
import CampingFilters from '../components/filters/camping/CampingFilters';
import AdditionalPreferences from '../components/filters/camping/AdditionalPreferences';
import InventoryTypeFilters from '../components/filters/inventoryTypes';
import { SearchFilterActivityTags } from '../components/SearchFilterActivityTags';
import { getFgActiveFiltersForGa } from '../utils/filters';
import { TripPreferencesBar } from '../components/tripPreferences/TripPreferencesBar';
import { TripPreferenceProvider } from '../components/tripPreferences/TripPreferenceContext';

import styles from './SearchFilters.module.scss';

class SearchFilters extends React.Component {
    static propTypes = {
        account: PropTypes.object,
        uiFilters: PropTypes.object,
        search: PropTypes.object,
        searchAggregations: PropTypes.object,
        dispatch: PropTypes.func,
        onApplyFilters: PropTypes.func,
        searchResults: PropTypes.object,
        flags: PropTypes.object,
        screenWidth: PropTypes.number,
    };

    static defaultProps = {};

    static displayName = 'SearchFilters';

    constructor(props) {
        super(props);
        this.onApplyFilter = this.onApplyFilter.bind(this);
        this.onApplyFilters = this.onApplyFilters.bind(this);
        this.onFilterToggle = this.onFilterToggle.bind(this);
        this.onFilterChange = this.onFilterChange.bind(this);
        this.onFilterRemove = this.onFilterRemove.bind(this);
        this.onResetFilters = this.onResetFilters.bind(this);
        this.onHideFilters = this.onHideFilters.bind(this);
        this.onUpdateSearchCriteria = this.onUpdateSearchCriteria.bind(this);
    }

    onResetFilters() {
        this.props.dispatch(
            searchActions.updateSearchCriterias({
                checkin_time: '',
                checkout_time: '',
                avail_nights: '',
                avail_months: [],
                tripPreferences: false,
                fg: [],
                ai_query: '',
            })
        );

        this.props.dispatch(searchActions.resetSearchFilters());
        this.props.dispatch(searchActions.fetchInventoryTypesAggregations());
        this.props.dispatch(searchActions.fetchSearchFiltersAggregations());
    }
    /** applies filters selected in dialog. Sends dispatch to close filter dialog */
    onApplyFilters() {
        this.props.dispatch(searchActions.updateSearchCriteria('filtersVisible', false));
        if (this.props.onApplyFilters) {
            this.props.onApplyFilters();
        }
    }

    onApplyFilter() {
        setTimeout(() => {
            if (this.props.onApplyFilters) {
                this.props.onApplyFilters();
            }
        }, 200);
    }

    onFilterToggle(filterName, item) {
        this.props.dispatch(searchActions.toggleSearchFilter(filterName, item));
    }

    onFilterChange(filterName, item) {
        this.props.dispatch(searchActions.setSearchFilter(filterName, item));
    }

    onFilterRemove(filterName, item) {
        this.props.dispatch(searchActions.removeSearchFilter(filterName, item));
    }

    onUpdateSearchCriteria(name, value) {
        this.props.dispatch(searchActions.updateSearchCriteria(name, value));
    }

    onHideFilters() {
        this.props.dispatch(searchActions.updateSearchCriteria('filtersVisible', false));
        // set focus back on dialog toggle after close
        document.getElementById('search-header-filters-btn').focus();
    }

    getInventoryFiltersCount = () => {
        let ret = 0;
        if (this.props.search && this.props.search.uiFilters) {
            ret += Object.keys(this.props.search.uiFilters.inventoryTypes).length;
        }
        return ret;
    };

    getCampsiteFilterCount = () => {
        let campsiteFilterCount = 0;
        if (this.props.search.fg && this.props.search.fg.length > 0) {
            campsiteFilterCount += this.props.search.fg.length;
        }
        if (this.props.search && this.props.search.uiFilters) {
            campsiteFilterCount += Object.keys(
                this.props.search.uiFilters.campsiteVehicleLength
            ).length;
            campsiteFilterCount += Object.keys(
                this.props.search.uiFilters.occupants
            ).length;
        }
        return campsiteFilterCount;
    };

    getTourFilterCount = () => {
        let tourFilterCount = 0;
        if (this.props.search && this.props.search.uiFilters) {
            tourFilterCount += Object.keys(this.props.search.uiFilters.tourTypes).length;
            tourFilterCount += Object.keys(
                this.props.search.uiFilters.tourAccessible
            ).length;
            tourFilterCount += Object.keys(
                this.props.search.uiFilters.tourDifficulty
            ).length;
            tourFilterCount += Object.keys(
                this.props.search.uiFilters.tourDuration
            ).length;
            tourFilterCount += Object.keys(this.props.search.uiFilters.tourPrice).length;
            tourFilterCount += Object.keys(this.props.search.uiFilters.tourTime).length;
            tourFilterCount += Object.keys(this.props.search.uiFilters.tourTimes).length;
        }
        return tourFilterCount;
    };

    getActivitiesFilterCount = () => {
        let activitiesFilterCount = 0;

        if (this.props.search && this.props.search.uiFilters) {
            activitiesFilterCount += Object.keys(
                this.props.search.uiFilters.activities
            ).length;
        }
        return activitiesFilterCount;
    };

    getActivityTagsFilterCount = () => {
        if (this.props.search?.uiFilters?.activityTags) {
            return Object.keys(this.props.search.uiFilters.activityTags).length;
        }
        return 0;
    };

    getPermitFilterCount = () => {
        let permitFilterCount = 0;

        if (this.props.search && this.props.search.uiFilters) {
            permitFilterCount += Object.keys(
                this.props.search.uiFilters.permitTypes
            ).length;
        }
        return permitFilterCount;
    };

    getAgenciesFiltersCount = () => {
        let ret = 0;

        if (this.props.search && this.props.search.uiFilters) {
            ret += Object.keys(this.props.search.uiFilters.agencies).length;
        }
        return ret;
    };

    checkInventoryType = (value) => {
        const { search } = this.props;
        if (search.fg) {
            return search.fg?.indexOf(value) !== -1;
        }
        return false;
    };

    // Send Selected Filter Info to GA
    getActiveFilters = () => {
        const ShowFilter = (visibleFilterHeader, filterCategoryName, hasPipe = true) => {
            let filterSegment = '';

            if (Object.keys(this.props.search.uiFilters[filterCategoryName]).length > 0) {
                filterSegment = `${
                    hasPipe ? '|' : ''
                } ${visibleFilterHeader}: ${Object.keys(
                    this.props.search.uiFilters[filterCategoryName]
                )} `;
            }

            return filterSegment;
        };

        const ShowAvailabilityFilters = (visibleFilterHeader, filterType) => {
            let filterSegment = '';

            if (get(this.props.search, filterType, false) === false) {
                filterSegment = `| ${visibleFilterHeader} `;
            }

            return filterSegment;
        };

        const ShowSizeFilters = (visibleFilterHeader, filterType) => {
            let filterSegment = '';

            const getSize =
                this?.props?.search?.uiFilters[filterType][filterType]?.filters[0]
                    ?.filterValue || [];

            if (getSize.length > 0) {
                filterSegment = `| ${visibleFilterHeader}: ${filterType}: ${getSize} `;
            }

            return filterSegment;
        };

        let activeFilters = '';

        if (this.props.search && this.props.search.uiFilters) {
            // Camping
            activeFilters += ShowFilter('Booking Types', 'inventoryTypes', false);

            if (process.env?.SEARCH?.showFederalAgencies) {
                activeFilters += ShowFilter(
                    'Federal Agencies Offering Reservations',
                    'agencies',
                    false
                );
            }

            activeFilters += ShowFilter('Allowable Equipment', 'campsiteEquipment');
            activeFilters += ShowFilter('Recreation Site Type', 'campsiteTypes');
            activeFilters += ShowFilter('Amenities', 'campsiteAmenities');
            activeFilters += ShowFilter('Electrical Hookup', 'campsiteElectricityHookup');

            // Tours
            activeFilters += ShowFilter('Tour Types', 'tourTypes');
            activeFilters += ShowFilter('Tour Accessible', 'tourAccessible');
            activeFilters += ShowFilter('Level of Effort', 'tourDifficulty');

            // Permits
            if (process.env?.ALLOWED_INVENTORY_TYPES?.permits) {
                activeFilters += ShowFilter('Permits', 'permitTypes');
            }

            // Activities
            activeFilters += ShowFilter('Activities', 'activities');

            activeFilters += ShowAvailabilityFilters(
                'Hide Unavailable',
                'include_unavailable'
            );
            activeFilters += ShowAvailabilityFilters(
                'Hide First-come, First-served',
                'include_notreservable'
            );
            activeFilters += ShowAvailabilityFilters(
                'Hide Partially Available',
                'include_partially_available'
            );

            // Group & Vehicle Size
            activeFilters += ShowSizeFilters('Group Size', 'occupants');
            activeFilters += ShowSizeFilters(
                'Vehicle Length (ft)',
                'campsiteVehicleLength'
            );
        }

        const fg = get(this.props, 'search.fg', []);

        if (!isEmpty(fg)) {
            activeFilters += getFgActiveFiltersForGa(fg);
        }

        return activeFilters;
    };

    renderSearchFilters = (tourFilterCount) => {
        const activityTagsFilterCount = this.getActivityTagsFilterCount();
        const isCampingSearch = this.checkInventoryType('camping');
        const isDayUseSearch = this.checkInventoryType('dayuse');
        const isTicketsSearch = this.checkInventoryType('tickets');
        const isPermitsSearch = this.checkInventoryType('permits');
        const isPoiSearch = this.checkInventoryType('poi');
        const isVenueSearch = this.checkInventoryType('venues');
        const isActivityPassSearch = this.checkInventoryType('activitypass');
        const isTreePermitsSearch = this.checkInventoryType('treepermits');
        const isRecAreaSearch = this.checkInventoryType('recarea');
        const hasNoInventory =
            !isCampingSearch &&
            !isDayUseSearch &&
            !isTicketsSearch &&
            !isPermitsSearch &&
            !isPoiSearch &&
            !isVenueSearch &&
            !isActivityPassSearch &&
            !isTreePermitsSearch &&
            !isRecAreaSearch;
        return (
            <div className="search-filters-dropdown-body rounded-layout">
                <section>
                    <div className="search-filters-section-header rounded-layout mb-2">
                        <div className="search-filter-section__trip-preferences">
                            <TripPreferenceProvider>
                                <TripPreferencesBar account={this.props.account} />
                            </TripPreferenceProvider>
                        </div>
                    </div>
                </section>
                <section className="search-filters-type-wrap">
                    <div className="search-filters-filter-container pb-2">
                        <InventoryTypeFilters
                            aggregations={this.props.searchAggregations}
                            onApplyFilters={this.onApplyFilter}
                        />
                    </div>
                </section>
                {(isCampingSearch || isDayUseSearch || hasNoInventory) && (
                    <CampingFilters
                        uiFilters={this.props.search.uiFilters}
                        aggregations={this.props.searchAggregations}
                        onApplyFilters={this.onApplyFilter}
                        onFilterRemove={this.onFilterRemove}
                        onFilterChange={this.onFilterChange}
                        search={this.props.search}
                        hasDates={Boolean(
                            this.props.search?.checkin_time &&
                            this.props.search?.checkout_time
                        )}
                    />
                )}

                {this.props?.flags?.activityTagsMvp?.enabled && (
                    <section className="search-filters-type-wrap">
                        <div className="search-filters-section-header rounded-layout mb-1 mt-3">
                            <Heading
                                headingLevel={2}
                                appearance="h5"
                                className={styles.activitiesHeading}
                            >
                                Nearby Activities <Badge appearance="info">BETA</Badge>
                            </Heading>
                            <span>
                                {`${activityTagsFilterCount} ${
                                    activityTagsFilterCount === 1 ? 'Filter' : 'Filters'
                                } Selected`}
                            </span>
                        </div>
                        <div className="search-filters-filter-container pb-2 mb-4">
                            <p className={styles.activitiesDisclaimer}>
                                {this.props.screenWidth >= 992
                                    ? 'Please confirm desired activity is available at location before booking.'
                                    : 'Please confirm desired activity is available at location before booking.'}
                            </p>
                            <Divider className="mb-2" />
                            <SearchFilterActivityTags
                                uiFilters={this.props.search.uiFilters}
                                onFilterToggle={this.onFilterToggle}
                                onApplyFilters={this.onApplyFilter}
                            />
                        </div>
                    </section>
                )}

                {(isTicketsSearch || hasNoInventory) && (
                    <section className="search-filters-type-wrap">
                        <div className="search-filters-section-header rounded-layout mb-1 mt-3">
                            <Heading headingLevel={2} appearance="h5">
                                Tickets & Tours
                            </Heading>
                            <span>
                                {`${this.getTourFilterCount(tourFilterCount)} ${
                                    this.getTourFilterCount(tourFilterCount) === 1
                                        ? 'Filter'
                                        : 'Filters'
                                } Selected`}
                            </span>
                        </div>
                        <div className="search-filters-filter-container pb-2 mb-4">
                            <SearchFilterStartTime
                                uiFilters={this.props.search.uiFilters}
                                aggregations={this.props.searchAggregations}
                                onFilterToggle={this.onFilterToggle}
                                onApplyFilters={this.onApplyFilter}
                            />

                            <Divider className="mb-2" />

                            <SearchFilterTourType
                                uiFilters={this.props.search.uiFilters}
                                aggregations={this.props.searchAggregations}
                                onFilterToggle={this.onFilterToggle}
                                onApplyFilters={this.onApplyFilter}
                            />

                            <Divider className="mb-4" />

                            <div className="pb-3">
                                <SearchFilterTourDuration
                                    onFilterChange={this.onFilterChange}
                                    onFilterRemove={this.onFilterRemove}
                                    onApplyFilters={this.onApplyFilter}
                                    isDisabled={isEmpty(
                                        this.props.searchAggregations?.tourTypes
                                    )}
                                />
                            </div>
                        </div>
                    </section>
                )}

                <AdditionalPreferences
                    uiFilters={this.props.search.uiFilters}
                    aggregations={this.props.searchAggregations}
                    onFilterToggle={this.onFilterToggle}
                    onApplyFilters={this.onApplyFilter}
                    onFilterRemove={this.onFilterRemove}
                    onFilterChange={this.onFilterChange}
                    search={this.props.search}
                    hideActivities={!!this.props?.flags?.activityTagsMvp?.enabled}
                />
            </div>
        );
    };

    render() {
        const filtersCount =
            this.getInventoryFiltersCount() +
            this.getCampsiteFilterCount(this.campsiteFilterCount) +
            this.getActivitiesFilterCount(this.activitiesFilterCount) +
            this.getActivityTagsFilterCount() +
            this.getTourFilterCount(this.tourFilterCount) +
            this.getPermitFilterCount(this.permitFilterCount) +
            this.getAgenciesFiltersCount();

        const searchFilters = this.renderSearchFilters(filtersCount);
        const totalItems = get(this.props.searchResults, 'total', 0);

        // SHOW THE TOTAL NUMBER OF RESULTS ON THE APPLY BUTTON
        const totalItemLabel = totalItems === 1 ? 'Result' : 'Results';
        const totalItemText =
            totalItems > 0 ? `View ${totalItems} ${totalItemLabel}` : 'No Results';

        const clearBtn = (
            <Button
                appearance="link"
                iconAfterElement={<Icon iconName="loop" />}
                className="reset"
                id="hook-reset"
                onClick={this.onResetFilters}
            >
                Clear All Filters
            </Button>
        );

        return (
            <div
                id="search-filters-panel"
                role="region"
                aria-label="Search Filters"
                className={`search-filters-dropdown-wrap ${
                    this.props.search.filtersVisible ? 'search-filters-visible' : ''
                }`}
            >
                <h1 className="rec-sr-only">Search Filters</h1>
                <FlexRow hasGutters={false}>
                    <FlexCol>{searchFilters}</FlexCol>
                </FlexRow>

                <div className="search-filters-actions">
                    {filtersCount <= 0 ? '' : clearBtn}

                    <div className="search-close-cancel-wrap">
                        <Button
                            appearance="subtle"
                            id="hook-hide"
                            onClick={this.onHideFilters}
                        >
                            Cancel
                        </Button>

                        <Button
                            className="close-btn"
                            iconAfterElement={<Icon iconName="chevron-up" />}
                            id="hook-apply"
                            onClick={this.onApplyFilters}
                            clickTagCategory="Search Results Filters"
                            clickTagAction={`Filter By - ${
                                filtersCount <= 0
                                    ? 'No Filters Selected'
                                    : this.getActiveFilters()
                            }`}
                            clickTagLabel="View Results Button"
                        >
                            {filtersCount <= 0 ? 'Hide Filters' : totalItemText}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

function select(state) {
    return {
        search: state.search,
        account: get(state, 'auth.account', null),
        searchAggregations: state.searchAggregations,
        searchResults: state.searchResults,
    };
}

export default withLDConsumer()(connect(select)(SearchFilters));

// cSpell:ignore criterias, checkin, notreservable, dayuse, activitypass, treepermits, recarea
